import { HighlightOff, History, FormatListBulleted } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, ToggleButtonGroup } from '@mui/material';
import { Box } from '@mui/system';
import { LineItemIcon } from 'views/DocumentViewer/components/Icons';
import { IconMessageCircle2, IconPaperclip } from '@tabler/icons';
import { ActivateSaveButtonFrom, LogReportMode, PanelTitle } from '../utils';
import useIsAllowedByRecordType from 'hooks/useIsAllowedByRecordType';
import { useTheme } from '@mui/styles';
import { ITenant } from 'types/tenant';
import { FilePdf, OrderAndEye } from 'ui-component/icons';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { StyledTooltip } from 'ui-component/RecordView/components/StyledTooltip';
import FooterOptions from '../Footer';

export interface HeaderPanelProps {
    toggleLogReport?: () => void;
    changePanel: PanelTitle;
    logReportMode: LogReportMode;
    isCreate?: boolean;
    onToggleLineItems?: () => void;
    isObjectPanel?: boolean;
    onGoBack?: () => void;
    onToggleComments?: () => void;
    onToggleAttachments?: () => void;
    recordTypeId?: number;
    isAttachmentFieldAllowed: boolean;
    handleClickSave: () => Promise<void>;
    handleClickCancel: () => void;
    validateObjectChanges?: () => void;
    loading: boolean;
    isRecordView?: boolean;
    businessEntity?: Pick<ITenant, 'id' | 'name'> | null | undefined;
    recordDataId?: number;
    onToggleRecordView?: () => void;
    hideButtons?: boolean;
    hideLogReport?: boolean;
    setActivateRightHandPanel?: (value: boolean) => void;
    activateRightHandPanel: boolean;
    onToggleDataFocusView?: () => void;
    openDataFocusViewDialog?: boolean;
    from?: 'data_focus_view' | 'portal' | 'document_viewer';
    objectEditingRestricted?: boolean;
}

const SideBar = ({
    changePanel,
    logReportMode,
    toggleLogReport,
    isCreate,
    onToggleLineItems,
    onGoBack,
    isObjectPanel = false,
    onToggleComments,
    onToggleAttachments,
    recordTypeId,
    isAttachmentFieldAllowed,
    handleClickSave,
    handleClickCancel,
    validateObjectChanges,
    loading,
    isRecordView,
    businessEntity,
    recordDataId,
    onToggleRecordView,
    hideButtons,
    hideLogReport,
    setActivateRightHandPanel,
    activateRightHandPanel,
    onToggleDataFocusView,
    openDataFocusViewDialog,
    from,
    objectEditingRestricted
}: HeaderPanelProps) => {
    const theme = useTheme();
    const { search } = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(search), [search]);
    const recordId = queryParams.get('recordId') || '';
    const [pdfViewerAnchorEl, setPdfViewerAnchorEl] = useState<null | HTMLElement>(null);

    const { isAllowed: isAllowedToAccessLogReport } = useIsAllowedByRecordType('log report access', recordTypeId);
    const { isAllowed: isAllowedToAccessLineItems } = useIsAllowedByRecordType('line item access', recordTypeId);
    const { isAllowed: isAllowedToPdfViewer } = useIsAllowedByRecordType('pdf record view', recordTypeId);
    const { isAllowed: isAllowedDynamicDataFocusView } = useIsAllowedByRecordType('dynamic data views', recordTypeId);
    const { isAllowed: isAllowedToAccessEntities } = useIsAllowedByRecordType('entities access', recordTypeId);

    const handleSave = async () => {
        if (changePanel === PanelTitle.EditObject && validateObjectChanges) {
            validateObjectChanges();
        } else {
            handleClickSave();
        }
    };

    const activateRightHandPanelButton = useCallback(
        (isLogReport?: boolean) => {
            setActivateRightHandPanel?.(isLogReport ? true : !activateRightHandPanel);

            if (isLogReport) {
                toggleLogReport?.();
            }
        },
        [setActivateRightHandPanel, toggleLogReport, activateRightHandPanel]
    );

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            sx={{
                backgroundColor: '#F5F6F7',
                height: '100%',
                padding: changePanel === PanelTitle.LogReport ? 0 : 0.5,
                with: '100%'
            }}
            data-testid="header-panel"
        >
            <Box gap={2} width="95%" display="flex" flexDirection="column" justifyContent="space-between">
                {changePanel !== PanelTitle.EditObject && (
                    <>
                        {from === ActivateSaveButtonFrom.DocumentViewer && changePanel !== PanelTitle.LogReport && !isCreate && (
                            <Box>
                                <StyledTooltip title="Metadata">
                                    <IconButton
                                        onClick={() => activateRightHandPanelButton()}
                                        size="medium"
                                        sx={{
                                            color: theme.palette.primary[400],
                                            bgcolor: activateRightHandPanel ? theme.palette.secondary.light : '#FFF',
                                            fontWeight: 600,
                                            height: '100%',
                                            '&:hover': {
                                                color: theme.palette.primary[400],
                                                backgroundColor: theme.palette.primary[300]
                                            }
                                        }}
                                    >
                                        <FormatListBulleted
                                            sx={{
                                                color: activateRightHandPanel ? theme.palette.secondary.main : theme.palette.primary[400]
                                            }}
                                        />
                                    </IconButton>
                                </StyledTooltip>
                            </Box>
                        )}

                        {!isCreate && isAllowedToAccessLogReport && !hideLogReport && (
                            <>
                                {!!toggleLogReport && changePanel !== PanelTitle.LogReport && (
                                    <Box>
                                        <StyledTooltip title="Audit Log">
                                            <IconButton
                                                onClick={() => activateRightHandPanelButton(true)}
                                                value="Log report"
                                                size="medium"
                                                sx={{
                                                    color: theme.palette.primary[400],
                                                    bgcolor: '#FFF',
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        color: theme.palette.primary[400],
                                                        bgcolor: theme.palette.primary[300]
                                                    }
                                                }}
                                            >
                                                <History fontSize="medium" />
                                            </IconButton>
                                        </StyledTooltip>
                                    </Box>
                                )}
                            </>
                        )}

                        {changePanel !== PanelTitle.LogReport && (
                            <>
                                {!isCreate && !!onToggleComments && (
                                    <Box>
                                        <StyledTooltip title="Comments">
                                            <IconButton
                                                onClick={onToggleComments}
                                                size="medium"
                                                sx={{
                                                    color: theme.palette.primary[400],
                                                    bgcolor:
                                                        logReportMode === LogReportMode.Comments ? theme.palette.secondary.light : '#FFF',
                                                    fontWeight: 600,
                                                    height: '100%',
                                                    '&:hover': {
                                                        color: theme.palette.primary[400],
                                                        backgroundColor: theme.palette.primary[300]
                                                    }
                                                }}
                                            >
                                                <IconMessageCircle2
                                                    color={
                                                        logReportMode === LogReportMode.Comments
                                                            ? theme.palette.secondary.main
                                                            : theme.palette.primary[400]
                                                    }
                                                />
                                            </IconButton>
                                        </StyledTooltip>
                                    </Box>
                                )}

                                {!isCreate && !!onToggleAttachments && isAttachmentFieldAllowed && (
                                    <Box>
                                        <StyledTooltip title="Attachments">
                                            <IconButton
                                                onClick={onToggleAttachments}
                                                size="medium"
                                                sx={{
                                                    color: theme.palette.primary[400],
                                                    bgcolor:
                                                        logReportMode === LogReportMode.Attachments
                                                            ? theme.palette.secondary.light
                                                            : '#FFF',
                                                    fontWeight: 600,
                                                    height: '100%',
                                                    '&:hover': {
                                                        color: theme.palette.primary[400],
                                                        backgroundColor: theme.palette.primary[300]
                                                    }
                                                }}
                                            >
                                                <IconPaperclip
                                                    color={
                                                        logReportMode === LogReportMode.Attachments
                                                            ? theme.palette.secondary.main
                                                            : theme.palette.primary[400]
                                                    }
                                                />
                                            </IconButton>
                                        </StyledTooltip>
                                    </Box>
                                )}

                                {!!onToggleLineItems && isAllowedToAccessLineItems && (
                                    <Box>
                                        <StyledTooltip title="Line Items">
                                            <IconButton
                                                onClick={onToggleLineItems}
                                                value="line items"
                                                size="medium"
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: theme.palette.primary[400],
                                                    bgcolor:
                                                        logReportMode === LogReportMode.LineItems ? theme.palette.secondary.light : '#FFF',
                                                    fontWeight: 600,
                                                    height: '100%',
                                                    '&:hover': {
                                                        color: theme.palette.primary[400],
                                                        backgroundColor: theme.palette.primary[300]
                                                    }
                                                }}
                                            >
                                                <LineItemIcon
                                                    color={
                                                        logReportMode === LogReportMode.LineItems
                                                            ? theme.palette.secondary.main
                                                            : theme.palette.primary[400]
                                                    }
                                                />
                                            </IconButton>
                                        </StyledTooltip>
                                    </Box>
                                )}

                                {!isCreate && !!onToggleRecordView && isAllowedToPdfViewer && (
                                    <Box>
                                        <StyledTooltip title="PDF View">
                                            <IconButton
                                                onClick={onToggleRecordView}
                                                onContextMenu={(e) => {
                                                    e.preventDefault();
                                                    setPdfViewerAnchorEl(e.currentTarget);
                                                }}
                                                size="medium"
                                                sx={{
                                                    color: theme.palette.primary[400],
                                                    backgroundColor: '#FFF',
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        color: theme.palette.primary[400],
                                                        backgroundColor: theme.palette.primary[300]
                                                    }
                                                }}
                                            >
                                                <FilePdf sx={{ color: theme.palette.primary[400] }} />
                                            </IconButton>
                                        </StyledTooltip>
                                        <Menu
                                            anchorEl={pdfViewerAnchorEl}
                                            open={!!pdfViewerAnchorEl}
                                            onClose={() => setPdfViewerAnchorEl(null)}
                                            PaperProps={{ sx: { borderRadius: '8px', bgcolor: '#f5f6f7' } }}
                                            anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
                                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        >
                                            <MenuItem
                                                onClick={() => {
                                                    setPdfViewerAnchorEl(null);
                                                    onToggleRecordView();
                                                }}
                                            >
                                                Open in a Modal
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => {
                                                    setPdfViewerAnchorEl(null);
                                                    const tenantUrl = localStorage.getItem('tenantPath');
                                                    window.open(
                                                        `${tenantUrl}/accounting/doc-viewer/?recordId=${recordId}&recordType=${recordTypeId}`,
                                                        '_blank'
                                                    );
                                                }}
                                            >
                                                Open in a new tab
                                            </MenuItem>
                                        </Menu>
                                    </Box>
                                )}

                                {!isCreate && isRecordView && isAllowedDynamicDataFocusView && (
                                    <Box>
                                        <StyledTooltip title="Data Focus View">
                                            <IconButton
                                                onClick={onToggleDataFocusView}
                                                size="medium"
                                                sx={{
                                                    color: theme.palette.primary[400],
                                                    bgcolor: openDataFocusViewDialog ? theme.palette.secondary.light : '#FFF', // fixes the minor bug
                                                    fontWeight: 600,
                                                    height: '100%',
                                                    '&:hover': {
                                                        color: theme.palette.primary[400],
                                                        backgroundColor: theme.palette.primary[300]
                                                    }
                                                }}
                                            >
                                                <OrderAndEye
                                                    sx={{
                                                        color: openDataFocusViewDialog // fixes the minor bug
                                                            ? theme.palette.secondary.main
                                                            : theme.palette.primary[400]
                                                    }}
                                                />
                                            </IconButton>
                                        </StyledTooltip>
                                    </Box>
                                )}

                                {isCreate && isObjectPanel && (
                                    <Box>
                                        <ToggleButtonGroup
                                            sx={{
                                                display: { xs: 'flex', md: 'flex' },
                                                justifyContent: 'flex-end',
                                                '& button': { height: '100%' }
                                            }}
                                        >
                                            <StyledTooltip title="Close Object Panel">
                                                <IconButton
                                                    onClick={() => onGoBack?.()}
                                                    value="Close Object Panel"
                                                    size="medium"
                                                    sx={{
                                                        color: theme.palette.primary[400],
                                                        backgroundColor: '#FFF',
                                                        fontWeight: 600,
                                                        '&:hover': {
                                                            color: theme.palette.primary[400],
                                                            backgroundColor: theme.palette.primary[300]
                                                        }
                                                    }}
                                                >
                                                    <HighlightOff />
                                                </IconButton>
                                            </StyledTooltip>
                                        </ToggleButtonGroup>
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                )}
            </Box>

            {!hideButtons && !(changePanel === PanelTitle.ViewObject || changePanel === PanelTitle.LogReport) && (
                <FooterOptions
                    panelState={changePanel}
                    onSave={handleSave}
                    onCancel={handleClickCancel}
                    isCreate={isCreate}
                    disabledButtons={loading}
                    businessEntity={businessEntity}
                    recordTypeId={recordTypeId}
                    recordDataId={recordDataId}
                    from={from}
                    objectEditingRestricted={objectEditingRestricted}
                    allowedToAccessEntities={isAllowedToAccessEntities}
                />
            )}
        </Box>
    );
};

export default SideBar;
